import { Controller } from "@hotwired/stimulus";
import * as VoePay from "../../utils/voe_pay";

// Keeps the VoE call page's log form in step with the dialer without reloading the dialers: the form frame
// loads once a dial creates its event, and the status frame refreshes when the call ends, polling until
// Twilio reports a terminal status.
export default class extends Controller {
  static targets = [
    "formFrame",
    "statusFrame",
    "status",
    "save",
    "orderClosed",
    "lineResult",
    "lineResultField",
    "outcome",
    "note",
    "noteRequired",
    "captureField",
    "captureToggle",
    "capture",
    "structure",
    "hoursField",
    "payAmount",
    "payAmountLabel",
    "payAmountError",
    "payHours",
    "payHoursError",
    "payReadout",
    "payFormula",
    "payAnnual",
  ];

  static values = {
    pageUrl: String,
    interval: { type: Number, default: 2000 },
    maxPolls: { type: Number, default: 15 },
    settleDelay: { type: Number, default: 3000 },
  };

  connect() {
    this.live = false;
    this.polls = 0;
    this.onCallStarted = () => this.callStarted();
    this.onCallEnded = () => this.callEnded();
    window.addEventListener("dialer:call-started", this.onCallStarted);
    window.addEventListener("dialer:call-ended", this.onCallEnded);
  }

  disconnect() {
    window.removeEventListener("dialer:call-started", this.onCallStarted);
    window.removeEventListener("dialer:call-ended", this.onCallEnded);
    clearTimeout(this.timer);
    clearTimeout(this.settleTimer);
  }

  callStarted() {
    this.live = true;
    this.settling = false;
    clearTimeout(this.settleTimer);
    this.#lockWhileLive();
    if (!this.formFrameTarget.querySelector("form")) this.#reload(this.formFrameTarget);
  }

  // The line result stays locked briefly after hang-up so Twilio's result can land first, but never longer than
  // settleDelay, since a late callback would otherwise hold up logging.
  callEnded() {
    this.live = false;
    this.polls = 0;
    this.settling = true;
    this.settleTimer = setTimeout(() => this.#settled(), this.settleDelayValue);
    this.#lockWhileLive();
    if (this.hasStatusFrameTarget) this.#reload(this.statusFrameTarget);
  }

  lineResultTargetConnected() {
    this.#lockWhileLive();
  }

  saveTargetConnected() {
    this.#lockWhileLive();
  }

  noteTargetConnected() {
    this.syncNote();
  }

  outcomeTargetConnected() {
    this.syncOutcome();
  }

  syncOutcome() {
    if (!this.hasOutcomeTarget) return;

    if (this.#capturing() && !this.lineResultTarget.value) {
      const { captureWhen } = this.captureFieldTarget.dataset;
      if (this.#hasOption(this.lineResultTarget, captureWhen)) this.lineResultTarget.value = captureWhen;
    }
    const select = this.outcomeTarget;
    if (this.outcomesSelect !== select) {
      this.outcomesSelect = select;
      this.outcomeLayout = Array.from(select.children)
        .filter((node) => node.value !== "")
        .map((node) => ({ node, options: node.tagName === "OPTGROUP" ? Array.from(node.children) : [node] }));
    }
    const lineResult = this.#lineResult();
    const allowed = JSON.parse(select.dataset.outcomesByLineResult || "{}")[lineResult] || [];
    const picked = select.value;

    this.outcomeLayout.forEach(({ node }) => node.remove());
    this.outcomeLayout.forEach(({ node, options }) => {
      const shown = options.filter((option) => allowed.includes(option.value));
      if (shown.length === 0) return;
      if (node.tagName === "OPTGROUP") node.replaceChildren(...shown);
      select.append(node);
    });
    select.value = allowed.includes(picked) ? picked : "";
    select.disabled = allowed.length === 0;
    select.required = allowed.length > 0;
    const blank = select.querySelector('option[value=""]');
    if (blank) blank.textContent = this.#blankOutcomeLabel(lineResult, allowed);
    this.#followCapture(allowed);
    this.syncNote();
  }

  syncNote() {
    if (!this.hasOutcomeTarget || !this.hasNoteTarget) return;

    const required = this.outcomeTarget.value === this.outcomeTarget.dataset.noteRequiredFor;
    this.noteTarget.required = required;
    this.noteTarget.placeholder = required ? "Say what happened on the call" : "";
    if (this.hasNoteRequiredTarget) this.noteRequiredTarget.hidden = !required;
  }

  toggleCapture() {
    const { captureWhen, captureOutcome } = this.captureFieldTarget.dataset;
    if (this.#capturing()) {
      this.lineResultTarget.value = captureWhen;
      this.syncOutcome();
      this.outcomeTarget.value = captureOutcome;
    } else if (!this.captureToggleTarget.checked && this.outcomeTarget.value === captureOutcome) {
      this.outcomeTarget.value = "";
    }
    this.syncNote();
    this.#reveal(this.captureTarget, this.captureToggleTarget.checked);
  }

  syncCapture() {
    if (!this.hasCaptureToggleTarget) return;

    this.captureToggleTarget.checked = this.outcomeTarget.value === this.captureFieldTarget.dataset.captureOutcome;
    this.#reveal(this.captureTarget, this.captureToggleTarget.checked);
  }

  toggleHours() {
    this.#reveal(this.hoursFieldTarget, this.structureTarget.value === "hourly");
  }

  payFormulaTargetConnected() {
    this.refreshPay();
  }

  refreshPay(event) {
    const structure = this.structureTarget.value || null;
    const amount = VoePay.parseAmount(this.payAmountTarget.value);
    const hours = parseFloat(this.payHoursTarget.value);
    const annual = VoePay.annualize(structure, amount, hours);
    this.payReadoutTarget.hidden = structure === null;
    this.payFormulaTarget.textContent =
      annual === null ? VoePay.promptText(structure) : VoePay.formulaText(structure, amount, hours);
    this.payAnnualTarget.textContent = annual === null ? "" : `${VoePay.currency(annual)} / yr`;
    this.payAmountLabelTarget.textContent = VoePay.amountLabel(structure);
    const typing = event?.type === "input";
    const amountProblem = VoePay.amountProblem(this.payAmountTarget.value);
    const hoursProblem = VoePay.hoursProblem(this.payHoursTarget.value, this.payHoursTarget.validity.badInput);
    VoePay.flag(this.payAmountTarget, this.payAmountErrorTarget, amountProblem, typing);
    VoePay.flag(this.payHoursTarget, this.payHoursErrorTarget, hoursProblem, typing);
  }

  statusTargetConnected(element) {
    this.#syncLineResult(element);
    this.syncOutcome();
    if (this.settling && !element.hasAttribute("data-pending")) this.#settled();
    clearTimeout(this.timer);
    if (this.live || !element.hasAttribute("data-pending") || this.polls >= this.maxPollsValue) return;

    this.timer = setTimeout(() => {
      this.polls += 1;
      if (this.hasStatusFrameTarget) this.#reload(this.statusFrameTarget);
    }, this.intervalValue);
  }

  // A good response without the status frame means the dial got logged (by Twilio's callback), so the form
  // reloads. An error response keeps what the analyst has typed.
  statusMissing(event) {
    event.preventDefault();
    if (event.detail.response.ok) this.#reload(this.formFrameTarget);
  }

  // No form once the call is over means nothing is left to log, so dialing can resume.
  formLoaded() {
    if (!this.live && !this.formFrameTarget.querySelector("form")) {
      window.dispatchEvent(new CustomEvent("voe:call-log-cleared"));
    }
  }

  orderClosedTargetConnected() {
    window.dispatchEvent(new CustomEvent("voe:order-closed"));
    if (this.hasStatusFrameTarget) this.#reload(this.statusFrameTarget);
  }

  // The form renders once, so the line result's choices, required state and blank label follow each status
  // refresh. The options are rebuilt from the full set the form rendered with, kept on first sync.
  #syncLineResult(status) {
    if (!this.hasLineResultTarget) return;

    const select = this.lineResultTarget;
    if (this.lineResultsSelect !== select) {
      this.lineResultsSelect = select;
      this.allLineResults = Array.from(select.options).filter((option) => option.value);
    }
    const pickable = JSON.parse(status.dataset.pickable || "[]");
    const picked = select.value;

    this.allLineResults.forEach((option) => option.remove());
    this.allLineResults.filter((option) => pickable.includes(option.value)).forEach((option) => select.add(option));
    select.value = pickable.includes(picked) ? picked : "";
    select.required = status.dataset.lineResultRequired === "true";
    const blank = select.querySelector('option[value=""]');
    if (blank) blank.textContent = status.dataset.blankLabel;
    if (this.hasLineResultFieldTarget) this.lineResultFieldTarget.hidden = pickable.length === 0;
    if (this.hasCaptureFieldTarget) {
      this.#reveal(this.captureFieldTarget, pickable.includes(this.captureFieldTarget.dataset.captureWhen));
    }
  }

  #lineResult() {
    if (this.hasLineResultTarget && this.lineResultTarget.value) return this.lineResultTarget.value;

    return this.hasStatusTarget ? this.statusTarget.dataset.lineResult : "";
  }

  #blankOutcomeLabel(lineResult, allowed) {
    if (this.live || this.settling) return "Available after the call";
    if (allowed.length > 0) return "Choose an outcome";
    if (lineResult) return "No outcome for this line result";

    return this.hasLineResultFieldTarget && this.lineResultFieldTarget.hidden
      ? "No outcome for this call"
      : "Pick a line result first";
  }

  #settled() {
    clearTimeout(this.settleTimer);
    this.settling = false;
    this.#lockWhileLive();
  }

  #lockWhileLive() {
    const locked = this.live || this.settling;
    if (this.hasSaveTarget) this.saveTarget.disabled = locked;
    if (this.hasLineResultTarget) this.lineResultTarget.disabled = locked;
    this.syncOutcome();
  }

  // Once outcomes can be picked, a switched-on capture fills a blank outcome with Verified, and switches off if
  // the line result rules Verified out.
  #followCapture(allowed) {
    if (!this.#capturing()) return;

    const { captureOutcome } = this.captureFieldTarget.dataset;
    if (allowed.includes(captureOutcome)) {
      if (!this.outcomeTarget.value) this.outcomeTarget.value = captureOutcome;
    } else if (this.#lineResult()) {
      this.captureToggleTarget.checked = false;
    }
    this.#reveal(this.captureTarget, this.captureToggleTarget.checked);
  }

  #capturing() {
    return this.hasCaptureToggleTarget && this.captureToggleTarget.checked && !this.live && !this.settling;
  }

  #hasOption(select, value) {
    return Array.from(select.options).some((option) => option.value === value);
  }

  #reveal(fieldset, shown) {
    fieldset.hidden = !shown;
    fieldset.disabled = !shown;
  }

  #reload(frame) {
    if (frame.src) {
      frame.reload();
    } else {
      frame.src = this.pageUrlValue;
    }
  }
}
